import { zodResolver } from '@hookform/resolvers/zod';
import { filterFixedProducts, filterUsageProducts } from '@weft/services';
import { isEmptyString, isNotEmptyString, isNotNullOrUndefined } from '@weft/utils';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useFormPersist from 'react-hook-form-persist';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { ErrorLabel } from '../../../../../components/design/ErrorLabel';
import { Spinner } from '../../../../../components/design/Spinner';
import { LocalStorageKeys } from '../../../../../constants/local-storage-keys';
import { useCreatePlan } from '../../../../../hooks/requests/use-create-plan';
import { usePlanById } from '../../../../../hooks/requests/use-plan-by-id';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { processRateMultiplierPlanProducts } from '../../../../../services/rate-multiplier-product-processor';
import { CREATE_PLAN_DEFAULT_VALUES } from '../../create-plan-page-consts/create-plan-default-values';
import { CREATE_PLAN_MUTATION_KEY } from '../../create-plan-page-consts/create-plan-mutation-key';
import type { NewPlanForm } from './create-plan-form-types/new-plan-form';
import type { PlanInputForm } from './create-plan-form-types/plan-input-form';
import { PlanInputFormSchema } from './create-plan-form-types/plan-input-form';

export const CreatePlanForm: React.FC<React.PropsWithChildren> = ({ children }) => {
  const navigate = useVyNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const cloneFromPlanId = searchParams.get('cloneFromPlanId') ?? undefined;

  const { mutateAsync: savePlanMutation } = useCreatePlan({
    mutationKey: CREATE_PLAN_MUTATION_KEY,
  });

  const { data: templatePlanResponse, isLoading: isLoadingPlan } = usePlanById(cloneFromPlanId ?? '', {
    enabled: isNotEmptyString(cloneFromPlanId),
    suspense: false,
    refetchOnWindowFocus: false,
  });

  const methods = useForm<PlanInputForm, unknown, NewPlanForm>({
    resolver: zodResolver(PlanInputFormSchema),
    defaultValues: CREATE_PLAN_DEFAULT_VALUES,
  });

  useFormPersist(LocalStorageKeys.CreatePlan, {
    watch: methods.watch,
    setValue: methods.setValue,
    storage: window.localStorage,
  });

  useEffect(() => {
    const storedCloneFromPlanId = methods.watch('basedFromPlanId');

    if (isEmptyString(cloneFromPlanId) && isNotNullOrUndefined(storedCloneFromPlanId)) {
      navigate(`/plans/create?cloneFromPlanId=${storedCloneFromPlanId}`);
    }
  }, [methods, navigate, cloneFromPlanId]);

  useEffect(() => {
    if (isNotEmptyString(cloneFromPlanId) && isNotNullOrUndefined(templatePlanResponse)) {
      const { products, ...planWithoutProducts } = templatePlanResponse.plan;
      const rateMultiplierAdjustedProducts = processRateMultiplierPlanProducts(products);
      const fixedProducts = filterFixedProducts(rateMultiplierAdjustedProducts) as PlanInputForm['fixedProducts'];
      const usageProducts = filterUsageProducts(rateMultiplierAdjustedProducts) as PlanInputForm['usageProducts'];

      methods.reset({
        ...planWithoutProducts,
        name: `Copy of ${templatePlanResponse.plan.name}`,
        basedFromPlanId: cloneFromPlanId,
        fixedProducts,
        usageProducts,
      }, { keepDefaultValues: true });
    }
  }, [methods, cloneFromPlanId, templatePlanResponse]);

  if (isLoadingPlan) {
    return <Spinner/>;
  }

  const onSubmit = methods.handleSubmit(async (planData) => {
    await savePlanMutation(planData, {
      onSuccess: async ({ plan: savedPlan }) => {
        await queryClient.resetQueries(['contractPage']);
        await queryClient.resetQueries(['plan-overview']);
        localStorage.removeItem(LocalStorageKeys.CreatePlan);
        navigate(`/plans/${savedPlan.id}`);
      },
    });
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={onSubmit}
        className="flex h-full flex-col"
      >
        {children}
      </form>

      {isNotNullOrUndefined(methods.formState.errors.root) &&
        (<ErrorLabel errorMessage={methods.formState.errors.root?.message ?? ''}/>)
      }
    </FormProvider>
  );
};
